<template>
  <div class="pb-7">
    <div style="height: 13rem;" class="d-flex flex-column mx-3 mt-2 mb-5 rounded"
         v-for="({id, media, desc, verified_by, date}) in patientWallets">
      <div class="d-flex justify-content-end flex-shrink-0">
        <small>{{ $moment.utc(date).fromNow() }}</small>
      </div>

      <div class="shadow rounded">
        <agile class="flex-grow-1"
               dir="ltr"
               :nav-buttons="false"
               :initial-slide="0"
               :key="id">
          <template v-for="mediaObj in media">
            <component class="slide" :is="mediaObj.mime_type.split('/')[0] + '-thumbnail'"
                       :src="mediaObj.full_url"
                       @click="showMedia(mediaObj,media)"
                       :key="mediaObj.id"/>
          </template>

          <template slot="prevButton"><i class="fa fa-chevron-left"></i></template>
          <template slot="nextButton"><i class="fa fa-chevron-right"></i></template>
        </agile>

        <div class="d-flex align-items-center justify-content-between px-3 py-2 bg-light-skyler rounded">
          <div>
            <h6 class="mb-0">
              {{ desc }}
            </h6>

            <small v-if="verified_by" class="d-block text-success">Verified</small>
          </div>

          <i-bin @click="deleteWallet(id)" height="2em" width="2em"></i-bin>
        </div>
      </div>
    </div>

    <b-button v-b-modal:create-wallet
              class="fixed-bottom shadow rounded-pill p-2 bg-magenta border-magenta" :class="fab">
      <b-icon-plus scale="2"></b-icon-plus>
    </b-button>


    <b-modal @hide="hide" @close="hide" id="create-wallet" :title="t('Create wallet')"
             scrollable
             :no-close-on-esc="show"
             :no-close-on-backdrop="show"
             :hide-header-close="show"
             centered
             header-class="pl-4"
             footer-class="justify-content-center"
             content-class="border-radius-1">
      <b-overlay :show="show">
        <b-form id="wallet-form" @submit.prevent="submit">
          <b-form-group>
            <b-input-group>
              <b-form-input size="sm" v-model="patientWallet.desc" name="desc"
                            :placeholder="t('Description')"></b-form-input>
            </b-input-group>
          </b-form-group>

          <b-form-group>
            <b-input-group>
              <b-select size="sm" v-model="year" :required="dateRequired">
                <b-select-option :value="null" disabled>{{ t('Year') }}</b-select-option>
                <b-select-option v-for="year in YEARS" :value="year" :key="year">
                  {{ year }}
                </b-select-option>
              </b-select>

              <b-select class="mx-2" size="sm" v-model="month" :required="dateRequired">
                <b-select-option :value="null" disabled>{{ t('Month') }}</b-select-option>
                <b-select-option v-for="month in MONTHS" :value="month" :key="month">
                  {{ month }}
                </b-select-option>
              </b-select>

              <b-select size="sm" v-model="day" :required="dateRequired">
                <b-select-option :value="null" disabled>{{ t('Day') }}</b-select-option>
                <b-select-option v-for="day in DAYS" :value="day" :key="day">
                  {{ day }}
                </b-select-option>
              </b-select>
            </b-input-group>
          </b-form-group>

          <file-pond
              ref="pond"
              :allow-multiple="true"
          ></file-pond>
        </b-form>
      </b-overlay>
      <template v-slot:modal-footer="{cancel}">
        <b-button :disabled="show" @click="cancel" class="mx-2 px-4" pill variant="secondary">{{ t('Cancel') }}
        </b-button>
        <b-button :disabled="show" type="submit" form="wallet-form" class="mx-2 px-4" pill variant="primary">
          {{ t('Submit') }}
        </b-button>
      </template>
    </b-modal>


    <infinite-loading @infinite="load">
      <span slot="no-more"></span>
      <span slot="no-results"></span>
    </infinite-loading>

    <span slot="no-results" class="position-absolute absolute-center"
          v-if="complete && patientWallets.length === 0">{{ t('No results') }}</span>

    <viewer @change="changeMedia" @close="closeMedia" :media="mediaToShow" :media-list="mediaListToShow"></viewer>
  </div>
</template>

<script>
import InfiniteLoading from 'vue-infinite-loading'
import IBin from "../components/Icons/IBin";
import Viewer from "../components/Viewer";
import ApplicationThumbnail from "@/components/MediaThumbnails/ApplicationThumbnail";
import VideoThumbnail from "@/components/MediaThumbnails/VideoThumbnail";
import ImageThumbnail from "@/components/MediaThumbnails/ImageThumbnail";
import {VueAgile} from 'vue-agile';
import vueFilePond from 'vue-filepond';
import 'filepond/dist/filepond.min.css';
import 'filepond-plugin-image-preview/dist/filepond-plugin-image-preview.min.css'
import previewPlugin from 'filepond-plugin-image-preview'
import orientation from 'filepond-plugin-image-exif-orientation'

const FilePond = vueFilePond(orientation, previewPlugin);

export default {
  name: "Wallet",
  components: {
    InfiniteLoading,
    IBin,
    agile: VueAgile,
    FilePond,
    Viewer,
    ApplicationThumbnail,
    VideoThumbnail,
    ImageThumbnail,
  },
  data: () => ({
    patientWallets: [],
    patientWallet: {
      desc: '',
      date: null,
      attachments: [],
    },
    show: false,
    year: null,
    complete: false,
    day: null,
    month: null,
    MONTHS: (new Array(12)).fill(0).map((_, i) => i + 1),
    YEARS: (new Array((new Date().getFullYear()) - 1930)).fill(0).map((_, i) => (1930 + i + 1)),
    page: 0,
    mediaListToShow: null,
    mediaToShow: null
  }),
  computed: {
    fab() {
      return this.$translate.lang === 'en' ? 'fab' : 'fab-rtl';
    },

    DAYS() {
      if (this.month) {
        if (+this.month <= 7) {
          if (+this.month % 2 === 1) {
            return (new Array(31)).fill(0).map((_, i) => i + 1);
          } else {
            return (new Array(30)).fill(0).map((_, i) => i + 1);
          }
        } else {
          if (+this.month % 2 === 0) {
            return (new Array(31)).fill(0).map((_, i) => i + 1);
          } else {
            return (new Array(30)).fill(0).map((_, i) => i + 1);
          }
        }
      } else {
        return []
      }
    },

    dateRequired() {
      return Boolean(this.day) || Boolean(this.month) || Boolean(this.year)
    }
  },
  methods: {
    load(state) {
      this.dataRepo.patientWallets(this.page)
          .catch(err => this.$handleError(err))
          .then(data => {
            if (data) {
              this.patientWallets.push(...data.data)
              state.loaded();
              this.page++;
            }

            if (!data || data.data.length < 10) {
              state.complete();
              this.complete = true
            }
          })
    },

    hide() {
      this.patientWallet = {
        desc: '',
        date: null,
        attachments: [],
      }
      this.day = null
      this.month = null
      this.year = null
    },

    submit(evt) {
      const formData = new FormData(evt.target);
      this.year && this.month && this.day && formData.append('date', `${this.year}-${this.month}-${this.day}`);

      this.$refs.pond.getFiles().forEach(({file}, i) => {
        formData.append(`attachments[${i}]`, file)
      })

      this.show = true;

      this.dataRepo.addPatientWallet(formData)
          .then(data => {
            this.patientWallets.push(data.data);

            this.$bvModal.hide('create-wallet');

            this.$swal.fire({
              title: data.message,
              icon: 'success'
            }).then(()=>{
              this.$router.go(this.$router.currentRoute)
            });

          })
          .catch(err => this.$handleError(err))
          .finally(() => this.show = false)
    },

    deleteWallet(id) {
      this.$swal.fire({
        title: 'You won\'t be able to revert this, proceed?',
        icon: "warning",
        showCancelButton: true
      }).then(({isConfirmed}) => {
        if (isConfirmed) {
          this.dataRepo.deletePatientWallet(id)
              .catch(err => this.$handleError(err))
              .then(({message, success}) => {
                this.$swal.fire({
                  title: message,
                  icon: success ? "success" : "error"
                })

                if (success) {
                  const i = this.patientWallets.findIndex(patientWallet => patientWallet.id === id)

                  this.patientWallets.splice(i, 1);
                }
              })
        }
      })
    },

    showMedia(mediaObj, media) {
      if (mediaObj.mime_type.split('/')[0] === 'application' || mediaObj.mime_type.split('/')[1] === 'x-ms-asf') {
        window.open(mediaObj.full_url, '_blank')
      } else {
        this.mediaToShow = mediaObj
        this.mediaListToShow = media
      }
    },

    changeMedia(media) {
      if (media.mime_type.split('/')[0] === 'application' || media.mime_type.split('/')[1] === 'x-ms-asf') {
        window.open(media.full_url, '_blank')
      } else {
        this.mediaToShow = media
      }
    },

    closeMedia() {
      this.mediaToShow = null
      this.mediaListToShow = null
    }
  },
  inject: ['dataRepo']
}
</script>

<style scoped>
.fab {
  width: 2.9em;
  height: 2.9em;
  bottom: 4.5rem;
  left: auto;
  right: 1rem;
  position: fixed;
  z-index: 1030;
  border-radius: 50%;
}

.fab-rtl {
  width: 2.9em;
  height: 2.9em;
  bottom: 4.5rem;
  right: auto;
  left: 1rem;
  position: fixed;
  z-index: 1030;
  border-radius: 50%;
}

.slide {
  display: block;
  height: 9.5rem !important;
  -o-object-fit: cover;
  object-fit: cover;
  width: 100%;
}
</style>
